/**
 * KalendarzCiazy — ETAP 6 MVP "Kalendarz Ciąży Suki".
 *
 * Pierwsze dedykowane narzędzie hodowcy: rejestrujesz krycie → automatyczny
 * harmonogram ciąży (USG ~28, RTG ~55, kojec ~58, poród ~63 dni).
 *
 * Zakres MVP (zatwierdzony):
 *   - Lista aktywnych ciąż + historia
 *   - Formularz "Dodaj krycie" (suka z hodowli / reproduktor z listy lub ręcznie)
 *   - Karta ciąży: dzień X z 63, pasek postępu, kamienie milowe, timeline
 *   - Akcje: Edytuj / Usuń / Oznacz poród
 *
 * NIE RUSZAMY: litters / dogs / studs (tylko odczyt suk i reproduktorów).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarHeart, Plus, Pencil, Trash2, Check, Loader2, X,
  Stethoscope, ScanLine, Home as HomeIcon, Dog, Clock, History,
} from 'lucide-react';
import StudioShell from '@/components/studio/StudioShell';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { API } from '@/utils/api';

const GESTATION_DAYS = 63;
const ACCENT = 'var(--apisai-primary)';

/* ─── Kamienie milowe (dni od krycia) ─── */
const MILESTONES = [
  { key: 'usg',   day: 28, label: 'USG',                 icon: Stethoscope, hint: 'Potwierdzenie ciąży' },
  { key: 'rtg',   day: 55, label: 'RTG',                 icon: ScanLine,    hint: 'Liczenie szczeniąt' },
  { key: 'kojec', day: 58, label: 'Przygotowanie kojca', icon: HomeIcon,    hint: 'Spadek temperatury' },
  { key: 'porod', day: 63, label: 'Przewidywany poród',  icon: Dog,         hint: 'Termin rozwiązania' },
];

/* ─── HELPERY DAT ─── */
const toDate = (s) => { try { return new Date(`${String(s).slice(0, 10)}T00:00:00`); } catch { return null; } };
const fmtPL = (s) => { const d = toDate(s); return d ? d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; };
const addDays = (s, n) => { const d = toDate(s); if (!d) return null; d.setDate(d.getDate() + n); return d; };
const daysBetween = (from, to) => {
  const a = toDate(from); const b = to instanceof Date ? to : toDate(to);
  if (!a || !b) return 0;
  return Math.floor((b - a) / 86400000);
};

/* ─── Wylicz status ciąży (postęp + kamienie) ─── */
function derivePregnancy(p) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayNum = Math.max(0, Math.min(GESTATION_DAYS, daysBetween(p.mating_date, today)));
  const pct = Math.round((dayNum / GESTATION_DAYS) * 100);
  const milestones = MILESTONES.map((m) => {
    const dateObj = addDays(p.mating_date, m.day);
    const done = today >= dateObj;
    return { ...m, date: dateObj, done };
  });
  const next = milestones.find((m) => !m.done) || null;
  const daysToDue = daysBetween(today.toISOString().slice(0, 10), p.due_date);
  return { dayNum, pct, milestones, next, daysToDue };
}

/* ═════════ KARTA AKTYWNEJ CIĄŻY ═════════ */
function ActivePregnancyCard({ p, onEdit, onWhelp, onDelete }) {
  const d = useMemo(() => derivePregnancy(p), [p]);
  return (
    <div
      className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden"
      data-testid={`pregnancy-card-${p.id}`}
    >
      {/* Nagłówek karty */}
      <div className="p-5 pb-4 flex items-start justify-between gap-3 border-b border-zinc-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--apisai-primary-soft, #FFF3EC)' }}>
              <CalendarHeart className="w-5 h-5" style={{ color: ACCENT }} />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-zinc-900 truncate" data-testid={`pregnancy-dam-${p.id}`}>
                {p.dam_name}
              </h3>
              <p className="text-xs text-zinc-500 truncate">
                {p.breed ? `${p.breed} · ` : ''}Reproduktor: {p.sire_name || '—'}
                {p.sire_external ? ' (zewnętrzny)' : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEdit(p)} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500" title="Edytuj" data-testid={`pregnancy-edit-${p.id}`}>
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(p)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Usuń" data-testid={`pregnancy-delete-${p.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Postęp */}
      <div className="px-5 pt-4">
        <div className="flex items-end justify-between mb-2">
          <div>
            <span className="text-2xl font-extrabold text-zinc-900" data-testid={`pregnancy-day-${p.id}`}>Dzień {d.dayNum}</span>
            <span className="text-sm font-medium text-zinc-400"> z {GESTATION_DAYS}</span>
          </div>
          <div className="text-right">
            <p className="text-xs text-zinc-400">Przewidywany poród</p>
            <p className="text-sm font-bold text-zinc-900">{fmtPL(p.due_date)}</p>
          </div>
        </div>
        <div className="h-2.5 w-full rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${d.pct}%`, background: ACCENT }} data-testid={`pregnancy-progress-${p.id}`} />
        </div>
        {d.next && (
          <p className="mt-2.5 text-xs font-semibold flex items-center gap-1.5" style={{ color: ACCENT }} data-testid={`pregnancy-next-${p.id}`}>
            <Clock className="w-3.5 h-3.5" />
            Najbliższy kamień: {d.next.label} · {fmtPL(d.next.date.toISOString())}
          </p>
        )}
      </div>

      {/* Timeline kamieni milowych */}
      <div className="px-5 py-4 mt-2">
        <div className="grid grid-cols-4 gap-2">
          {d.milestones.map((m) => {
            const Icon = m.icon;
            const isNext = d.next && d.next.key === m.key;
            return (
              <div key={m.key} className="text-center" data-testid={`pregnancy-milestone-${m.key}-${p.id}`}>
                <div
                  className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-1.5 transition-colors"
                  style={
                    m.done
                      ? { background: '#ECFDF5', color: '#10B981' }
                      : isNext
                        ? { background: 'var(--apisai-primary-soft, #FFF3EC)', color: ACCENT, boxShadow: `0 0 0 2px ${ACCENT}` }
                        : { background: '#F4F4F5', color: '#A1A1AA' }
                  }
                >
                  {m.done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <p className={`text-[11px] font-semibold leading-tight ${m.done ? 'text-emerald-600' : isNext ? '' : 'text-zinc-500'}`} style={isNext ? { color: ACCENT } : undefined}>
                  {m.label}
                </p>
                <p className="text-[10px] text-zinc-400 mt-0.5">Dzień {m.day}</p>
                <p className="text-[10px] text-zinc-400">{fmtPL(m.date.toISOString())}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Oznacz poród */}
      <div className="px-5 pb-5">
        <Button
          onClick={() => onWhelp(p)}
          className="w-full rounded-xl h-10 text-white font-semibold text-sm"
          style={{ background: ACCENT }}
          data-testid={`pregnancy-whelp-${p.id}`}
        >
          <Dog className="w-4 h-4 mr-1.5" /> Oznacz poród
        </Button>
      </div>
    </div>
  );
}

/* ═════════ KARTA HISTORII ═════════ */
function HistoryCard({ p, onDelete }) {
  const isWhelped = p.status === 'whelped';
  return (
    <div className="bg-white rounded-xl border border-zinc-200 px-4 py-3 flex items-center justify-between gap-3" data-testid={`pregnancy-history-${p.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isWhelped ? 'bg-emerald-50 text-emerald-600' : 'bg-zinc-100 text-zinc-400'}`}>
          {isWhelped ? <Dog className="w-4 h-4" /> : <X className="w-4 h-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900 truncate">{p.dam_name} <span className="text-zinc-400 font-normal">× {p.sire_name || '—'}</span></p>
          <p className="text-xs text-zinc-500">
            {isWhelped
              ? `Poród: ${fmtPL(p.whelped_date || p.due_date)}`
              : `Anulowano · krycie ${fmtPL(p.mating_date)}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${isWhelped ? 'bg-emerald-50 text-emerald-600' : 'bg-zinc-100 text-zinc-500'}`}>
          {isWhelped ? 'Po porodzie' : 'Anulowana'}
        </span>
        <button onClick={() => onDelete(p)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Usuń">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ═════════ FORMULARZ "DODAJ KRYCIE" ═════════ */
const emptyForm = { dam_name: '', dam_id: null, sire_name: '', sire_stud_id: null, sire_external: false, breed: '', mating_date: '', notes: '' };

function MatingDialog({ open, onClose, onSaved, editing, suki, studs }) {
  const [form, setForm] = useState(emptyForm);
  const [damMode, setDamMode] = useState('select'); // 'select' | 'manual'
  const [sireMode, setSireMode] = useState('select');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({ ...emptyForm, ...editing });
        setDamMode(editing.dam_id ? 'select' : 'manual');
        setSireMode(editing.sire_external || !editing.sire_stud_id ? 'manual' : 'select');
      } else {
        setForm(emptyForm); setDamMode(suki.length ? 'select' : 'manual'); setSireMode('select');
      }
    }
  }, [open, editing, suki.length]);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const duedatePreview = useMemo(() => {
    if (!form.mating_date) return null;
    const d = addDays(form.mating_date, GESTATION_DAYS);
    return d ? d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  }, [form.mating_date]);

  const pickDam = (id) => {
    if (id === '__manual__') { setDamMode('manual'); setForm((f) => ({ ...f, dam_id: null, dam_name: '' })); return; }
    const dog = suki.find((s) => s.id === id);
    if (dog) setForm((f) => ({ ...f, dam_id: dog.id, dam_name: dog.name, breed: f.breed || dog.breed || '' }));
  };
  const pickSire = (id) => {
    if (id === '__manual__') { setSireMode('manual'); setForm((f) => ({ ...f, sire_stud_id: null, sire_name: '', sire_external: true })); return; }
    const dog = studs.find((s) => s.id === id);
    if (dog) setForm((f) => ({ ...f, sire_stud_id: dog.id, sire_name: dog.name, sire_external: false }));
  };

  const handleSubmit = async () => {
    if (!form.dam_name.trim()) { toast.error('Wybierz lub wpisz sukę'); return; }
    if (!form.mating_date) { toast.error('Podaj datę krycia'); return; }
    setSaving(true);
    try {
      const payload = {
        dam_name: form.dam_name.trim(), dam_id: form.dam_id,
        sire_name: form.sire_name.trim(), sire_stud_id: form.sire_stud_id,
        sire_external: sireMode === 'manual', breed: form.breed.trim(),
        mating_date: form.mating_date, notes: form.notes.trim(),
      };
      if (editing) await axios.patch(`${API}/pregnancies/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/pregnancies`, payload, { headers });
      toast.success(editing ? 'Zaktualizowano' : 'Dodano krycie');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Nie udało się zapisać');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--apisai-primary)]/30 focus:border-[var(--apisai-primary)]';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="mating-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edytuj krycie' : 'Dodaj krycie'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Suka */}
          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1.5">Suka *</label>
            {damMode === 'select' && suki.length > 0 ? (
              <select className={inputCls} value={form.dam_id || ''} onChange={(e) => pickDam(e.target.value)} data-testid="mating-dam-select">
                <option value="" disabled>Wybierz sukę z hodowli…</option>
                {suki.map((s) => <option key={s.id} value={s.id}>{s.name}{s.breed ? ` · ${s.breed}` : ''}</option>)}
                <option value="__manual__">➕ Inna (wpisz ręcznie)</option>
              </select>
            ) : (
              <div className="flex gap-2">
                <input className={inputCls} placeholder="Imię suki" value={form.dam_name} onChange={(e) => setForm((f) => ({ ...f, dam_name: e.target.value, dam_id: null }))} data-testid="mating-dam-input" />
                {suki.length > 0 && (
                  <button type="button" onClick={() => { setDamMode('select'); setForm((f) => ({ ...f, dam_name: '', dam_id: null })); }} className="text-xs text-zinc-500 px-2 whitespace-nowrap">Z listy</button>
                )}
              </div>
            )}
          </div>

          {/* Reproduktor */}
          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1.5">Reproduktor</label>
            {sireMode === 'select' ? (
              <select className={inputCls} value={form.sire_stud_id || ''} onChange={(e) => pickSire(e.target.value)} data-testid="mating-sire-select">
                <option value="" disabled>Wybierz reproduktora…</option>
                {studs.map((s) => <option key={s.id} value={s.id}>{s.name}{s.breed ? ` · ${s.breed}` : ''}</option>)}
                <option value="__manual__">➕ Reproduktor zewnętrzny (wpisz ręcznie)</option>
              </select>
            ) : (
              <div className="flex gap-2">
                <input className={inputCls} placeholder="Nazwa reproduktora (zewnętrzny)" value={form.sire_name} onChange={(e) => setForm((f) => ({ ...f, sire_name: e.target.value, sire_stud_id: null }))} data-testid="mating-sire-input" />
                <button type="button" onClick={() => { setSireMode('select'); setForm((f) => ({ ...f, sire_name: '', sire_stud_id: null, sire_external: false })); }} className="text-xs text-zinc-500 px-2 whitespace-nowrap">Z listy</button>
              </div>
            )}
          </div>

          {/* Data krycia */}
          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1.5">Data krycia *</label>
            <input type="date" className={inputCls} value={form.mating_date} onChange={(e) => setForm((f) => ({ ...f, mating_date: e.target.value }))} data-testid="mating-date-input" />
            {duedatePreview && (
              <p className="mt-1.5 text-xs text-zinc-500">Przewidywany poród: <span className="font-semibold" style={{ color: ACCENT }}>{duedatePreview}</span></p>
            )}
          </div>

          {/* Rasa */}
          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1.5">Rasa</label>
            <input className={inputCls} placeholder="np. Owczarek niemiecki" value={form.breed} onChange={(e) => setForm((f) => ({ ...f, breed: e.target.value }))} data-testid="mating-breed-input" />
          </div>

          {/* Notatki */}
          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1.5">Notatki</label>
            <textarea rows={2} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--apisai-primary)]/30 focus:border-[var(--apisai-primary)]" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="mating-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Anuluj</Button>
          <Button onClick={handleSubmit} disabled={saving} className="rounded-xl text-white font-semibold" style={{ background: ACCENT }} data-testid="mating-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {editing ? 'Zapisz' : 'Dodaj krycie'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═════════ STRONA ═════════ */
export default function KalendarzCiazy() {
  const [loading, setLoading] = useState(true);
  const [pregnancies, setPregnancies] = useState([]);
  const [suki, setSuki] = useState([]);
  const [studs, setStuds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [whelpTarget, setWhelpTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchPregnancies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pregnancies`, { headers });
      setPregnancies(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPregnancies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDogs = useCallback(async () => {
    try {
      const kRes = await axios.get(`${API}/kennels`, { headers });
      const kennels = kRes?.data?.items || kRes?.data || [];
      if (Array.isArray(kennels) && kennels.length) {
        const dRes = await axios.get(`${API}/kennels/${kennels[0].id}/dogs`, { headers });
        const dogs = Array.isArray(dRes.data) ? dRes.data : [];
        setSuki(dogs.filter((d) => (d.gender || '').toLowerCase() === 'female'));
      }
    } catch {}
    try {
      const sRes = await axios.get(`${API}/studs`, { headers });
      setStuds(Array.isArray(sRes.data) ? sRes.data : []);
    } catch {}
  }, []);

  useEffect(() => { fetchPregnancies(); fetchDogs(); }, [fetchPregnancies, fetchDogs]);

  const active = pregnancies.filter((p) => p.status === 'active');
  const history = pregnancies.filter((p) => p.status !== 'active');

  const confirmWhelp = async () => {
    if (!whelpTarget) return;
    try {
      await axios.patch(`${API}/pregnancies/${whelpTarget.id}`, {
        status: 'whelped', whelped_date: new Date().toISOString().slice(0, 10),
      }, { headers });
      toast.success('Oznaczono poród');
      fetchPregnancies();
    } catch { toast.error('Nie udało się zapisać'); }
    setWhelpTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/pregnancies/${deleteTarget.id}`, { headers });
      toast.success('Usunięto');
      fetchPregnancies();
    } catch { toast.error('Nie udało się usunąć'); }
    setDeleteTarget(null);
  };

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  return (
    <StudioShell mode="workspace" activeKey="kalendarz" contextLabel="Kalendarz ciąży">
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="kalendarz-ciazy-page">

        {/* Nagłówek */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2" style={{ background: 'var(--apisai-primary-soft, #FFF3EC)' }}>
              <CalendarHeart className="w-3.5 h-3.5" style={{ color: ACCENT }} />
              <span className="text-[12px] font-semibold" style={{ color: ACCENT }}>Narzędzie hodowcy</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900">Kalendarz Ciąży Suki</h1>
            <p className="text-sm text-zinc-500 mt-1">Zarejestruj krycie — sami policzymy terminy USG, RTG i porodu.</p>
          </div>
          <Button onClick={openAdd} className="rounded-xl text-white font-semibold flex-shrink-0" style={{ background: ACCENT }} data-testid="add-mating-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj krycie
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: ACCENT }} />
          </div>
        ) : pregnancies.length === 0 ? (
          /* Pusty stan */
          <div className="bg-white rounded-2xl border border-zinc-200 py-14 px-6 text-center" data-testid="kalendarz-empty">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--apisai-primary-soft, #FFF3EC)' }}>
              <CalendarHeart className="w-8 h-8" style={{ color: ACCENT }} />
            </div>
            <h3 className="text-lg font-bold text-zinc-900 mb-2">Brak aktywnych ciąż</h3>
            <p className="text-sm text-zinc-500 max-w-sm mx-auto mb-6">
              Dodaj pierwsze krycie, a my automatycznie zaplanujemy USG, RTG, przygotowanie kojca i przewidywany termin porodu.
            </p>
            <Button onClick={openAdd} className="rounded-xl h-11 px-7 text-white font-semibold" style={{ background: ACCENT }} data-testid="empty-add-mating-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Dodaj krycie
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Aktywne ciąże */}
            {active.length > 0 && (
              <section data-testid="active-pregnancies-section">
                <h2 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: ACCENT }} />
                  Aktywne ciąże ({active.length})
                </h2>
                <div className="space-y-4">
                  {active.map((p) => (
                    <ActivePregnancyCard key={p.id} p={p} onEdit={openEdit} onWhelp={setWhelpTarget} onDelete={setDeleteTarget} />
                  ))}
                </div>
              </section>
            )}

            {/* Historia */}
            {history.length > 0 && (
              <section data-testid="history-pregnancies-section">
                <h2 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-zinc-400" /> Historia ({history.length})
                </h2>
                <div className="space-y-2">
                  {history.map((p) => (
                    <HistoryCard key={p.id} p={p} onDelete={setDeleteTarget} />
                  ))}
                </div>
              </section>
            )}

            {active.length === 0 && history.length > 0 && (
              <p className="text-center text-sm text-zinc-400 py-2">Brak aktywnych ciąż — dodaj nowe krycie.</p>
            )}
          </div>
        )}
      </div>

      {/* Dialog formularza */}
      <MatingDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchPregnancies}
        editing={editing}
        suki={suki}
        studs={studs}
      />

      {/* Potwierdzenie: oznacz poród */}
      <AlertDialog open={!!whelpTarget} onOpenChange={(o) => { if (!o) setWhelpTarget(null); }}>
        <AlertDialogContent data-testid="whelp-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Oznaczyć poród?</AlertDialogTitle>
            <AlertDialogDescription>
              Ciąża suki <b>{whelpTarget?.dam_name}</b> zostanie przeniesiona do historii z dzisiejszą datą porodu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={confirmWhelp} className="rounded-xl text-white" style={{ background: ACCENT }} data-testid="whelp-confirm-btn">
              Oznacz poród
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Potwierdzenie: usuń */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć wpis?</AlertDialogTitle>
            <AlertDialogDescription>
              Wpis ciąży suki <b>{deleteTarget?.dam_name}</b> zostanie trwale usunięty.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-xl bg-red-500 hover:bg-red-600 text-white" data-testid="delete-confirm-btn">
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StudioShell>
  );
}
